import type { FC } from 'react';
import { Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StatusCard } from '@/components/ui/card/StatusCard';
import { useTranslation } from '@/hooks/useTranslation';

export type SaveStatus = 'idle' | 'saving' | 'ok' | 'error';

interface ConfigCardStatusFooterProps {
  isLoading: boolean;
  loadError: string | null;
  usingDefaults: boolean;
  saveStatus: SaveStatus;
  saveError: string | null;
  savedMessage: string;
  canSave: boolean;
  onRetry: () => void;
  onReset: () => void;
  onSave: () => void;
}

/**
 * Shared "using defaults / saved / error" status cards + retry/reset/save footer row of a
 * backend-config card (InfluxConfigCard today).
 */
export const ConfigCardStatusFooter: FC<ConfigCardStatusFooterProps> = ({
  isLoading,
  loadError,
  usingDefaults,
  saveStatus,
  saveError,
  savedMessage,
  canSave,
  onRetry,
  onReset,
  onSave,
}) => {
  const { t } = useTranslation();

  return (
    <>
      {usingDefaults && saveStatus !== 'ok' && (
        <StatusCard
          type="info"
          title={t('backendConfig.usingDefaults.title')}
          message={t('backendConfig.usingDefaults.body')}
        />
      )}

      {saveStatus === 'ok' && (
        <StatusCard type="success" title={t('backendConfig.saved')} message={savedMessage} />
      )}

      {saveStatus === 'error' && saveError && <StatusCard type="error" message={saveError} />}

      <div className="flex items-center justify-between gap-4 flex-wrap pt-1">
        <span className="text-xs text-muted-foreground">
          {isLoading && t('backendConfig.loading')}
        </span>
        <div className="flex gap-2.5">
          {!isLoading && loadError && (
            <Button variant="outline" size="sm" onClick={onRetry}>
              {t('backendConfig.retry')}
            </Button>
          )}
          {!isLoading && !loadError && (
            <>
              <Button variant="outline" size="sm" onClick={onReset} disabled={saveStatus === 'saving'}>
                {t('backendConfig.reset')}
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={onSave}
                disabled={!canSave}
                loading={saveStatus === 'saving'}
                aria-label={t('backendConfig.save')}
              >
                {saveStatus !== 'saving' && <Save aria-hidden />}
                {t('backendConfig.save')}
              </Button>
            </>
          )}
        </div>
      </div>
    </>
  );
};
